import Point from 'ol/geom/Point';
import CircleGeometry from 'ol/geom/Circle';
import LineString from 'ol/geom/LineString';
import Polygon from 'ol/geom/Polygon';
import { transform } from 'ol/proj';
import { intersects, containsCoordinate } from 'ol/extent';

import type { Ref } from 'vue';
import type { Coordinate } from 'ol/coordinate';
import type { Extent } from 'ol/extent';

import Consts from '@/api/Consts';
import Utils from '@/api/Utils';

import MapConsts from '@/components/map/consts/MapConsts';

import type { ServiceKey } from '@/utils/types/baseStationsTypes';
import type { GnssStatDisplayItem, GnssStatItem } from '@/components/map/types/gnssStatsTypes';
import type { GpsFixQualityItem } from '@/components/map/consts/MapConsts';
import type { GnssDataItem } from '@/api/Consts';
import type {
	Baseline,
	BaselineStatusOnline,
	FeatureWithConnectedBaseStation,
	LonLat,
	LonLatRadius,
	RectangleItem,
	RoverHistoryFragmentDisplay,
	ServiceColors,
	BaseStationStyleFeatureData,
} from '@/components/map/types/mapFeatureTypes';


type XYasArray = readonly [number, number];


const toXYasArray = (coord: Coordinate): XYasArray => {
	const [x, y] = coord;
	if (x === undefined || y === undefined) {
		throw new Error('Invalid coordinate length: ' + coord.length);
	}
	return [x, y];
};


const transformFromLonLat = (coords: Coordinate): Coordinate => transform(coords, 'EPSG:4326', 'EPSG:3857');
const transformFromLonLatArray = (points: Coordinate[]): Coordinate[] => points.map(coords => transformFromLonLat(coords));
const transformCoords = (item: LonLat): Coordinate => transformFromLonLat([item.lon, item.lat]);
const transformToLonLat = (coords: Coordinate): Coordinate => transform(coords, 'EPSG:3857', 'EPSG:4326');
const getPoint = (item: LonLat): Point => new Point(transformCoords(item));
const getLine = (start: LonLat, end: LonLat): LineString => new LineString([transformCoords(start), transformCoords(end)]);
const getCircle = (item: LonLatRadius): CircleGeometry => {
	const latRadians = (item.lat * Math.PI) / 180;
	const radiusInProjectionUnits = item.radius / Math.cos(latRadians);
	return new CircleGeometry(transformCoords(item), radiusInProjectionUnits);
};
const getRectangle = (item: RectangleItem): Polygon => new Polygon([[
	transformCoords({ lon: item.point2.lon, lat: item.point2.lat }),
	transformCoords({ lon: item.point1.lon, lat: item.point2.lat }),
	transformCoords({ lon: item.point1.lon, lat: item.point1.lat }),
	transformCoords({ lon: item.point2.lon, lat: item.point1.lat }),
	transformCoords({ lon: item.point2.lon, lat: item.point2.lat }),
]]);


const getServiceColors = (service: ServiceKey, active: boolean, isNew: boolean): ServiceColors => {
	const strokeColor = Consts.COLORS.featureStrokeColor;
	let fillColor: string = Consts.COLORS.dark0;
	if (service === MapConsts.NEAR_SERVICE) {
		fillColor = Consts.COLORS.nearServiceColor;
	}
	if (service === MapConsts.VRS_SERVICE) {
		fillColor = Consts.COLORS.vrsServiceColor;
	}
	let opacity: string = Consts.OPACITY.full;
	if (!active) {
		opacity = isNew ? Consts.OPACITY.half : Consts.OPACITY.low;
	}
	return [strokeColor + opacity, fillColor + opacity];
};

const BASELINE_ONLINE_ERROR_STATUSES: ReadonlySet<BaselineStatusOnline> = new Set([
	'broken',
	'offline',
]);

const BASELINE_ONLINE_PROCESSING_STATUSES: ReadonlySet<BaselineStatusOnline> = new Set([
	'no_data',
	'not_applicable',
	'no_fix',
]);


const getBaselineColor = (baseline: Baseline): string => {
	if (baseline.status === 'stopped' || baseline.deleted) {
		return Consts.COLORS.dark0;
	}
	if (baseline.updating) {
		return Consts.COLORS.processingUltraDark;
	}
	if (baseline.status === 'working') {
		if (BASELINE_ONLINE_ERROR_STATUSES.has(baseline.status_online)) {
			return Consts.COLORS.errorUltraDark;
		}
		if (BASELINE_ONLINE_PROCESSING_STATUSES.has(baseline.status_online)) {
			return Consts.COLORS.processingUltraDark;
		}
		return Consts.COLORS.successDarker;
	}
	return Consts.COLORS.errorUltraDark;
};


const getColor = (
	value: number,
	colorConverter: (value: number) => number,
	colors: readonly [string, ...string[]],
): string => {
	const colorIndex = Math.min(Math.max(Math.floor(colorConverter(value)), 0), colors.length - 1);
	return colors[colorIndex] ?? colors[0];
};


const getQualityColor = (value: number): string => {
	const safeValue = Math.min(Math.max(Number(value) || 0, 0), 1);
	const red = Math.round((1 - safeValue) * 255);
	const green = Math.round(safeValue * 200 + 55);
	return 'rgba(' + red + ', ' + green + ', 60, 0.70)';
};


const filterInsideViewport = (viewportExtent: Extent | null) => (item: RoverHistoryFragmentDisplay): boolean => {
	if (viewportExtent == null) {
		return true;
	}
	if (item.is_break) {
		return containsCoordinate(viewportExtent, transformFromLonLat([item.lon1, item.lat1]));
	}
	const [x1, y1] = toXYasArray(transformFromLonLat([item.lon1, item.lat1]));
	const [x2, y2] = toXYasArray(transformFromLonLat([item.lon2, item.lat2]));
	const lineExtent: Extent = [
		Math.min(x1, x2),
		Math.min(y1, y2),
		Math.max(x1, x2),
		Math.max(y1, y2),
	];
	return intersects(viewportExtent, lineExtent);
};


const filterVRSFeaturesRemoveDuplicateStations = <T extends FeatureWithConnectedBaseStation>(
	coords: T[],
): T[] => {
	const seenIds = new Set<number | string>();
	return coords.filter(item => {
		const cbs = item.connectedBaseStation;
		if (cbs == null || cbs.service !== MapConsts.VRS_SERVICE || seenIds.has(cbs.id)) {
			return false;
		}
		seenIds.add(cbs.id);
		return true;
	});
};


const checkIsFocusedMountpoint = (
	featureData: Pick<BaseStationStyleFeatureData, 'mntpnt_int' | 'mntpnt_src'>,
	focusedMountpoint: string,
): boolean => {
	return featureData.mntpnt_int === focusedMountpoint || featureData.mntpnt_src === focusedMountpoint;
};


const getGnssDataDisplay = (gnss: string): GnssDataItem => {
	const FALLBACK: GnssDataItem = {
		code: gnss,
		color: '999999',
		colorLight: 'cccccc',
		name: gnss,
		showInLegend: false,
		isActual: false,
	};
	return Utils.findItem(Consts.GNSS_DATA, gnss, 'code', FALLBACK);
};


const getGnssFixQualityDisplay = (code: number): GpsFixQualityItem => {
	const FALLBACK: GpsFixQualityItem = {
		code,
		color: '999999',
	};
	return Utils.findItem(MapConsts.GPS_FIX_QUALITY_DISPLAY, code, 'code', FALLBACK);
};


const getRelativeColumnHeight = (value: number, minValue: number | null | undefined, maxValue: number | null | undefined): number => {
	if (minValue == null || maxValue == null) {
		return value / 10;
	}
	const correctedValue = Math.min(Math.max(value, minValue), maxValue);
	return (correctedValue - minValue) / (maxValue - minValue);
};

const getGnssStatsDisplay = (
	statsRef: Ref<Record<string, GnssStatItem> | null>,
	fullModeRef: Ref<boolean>,
	allSubnetworksModeRef: Ref<boolean>,
): GnssStatDisplayItem[] => {
	if (statsRef.value == null) {
		return [];
	}
	const rawEntries = Object.entries(statsRef.value).map(([gnss, value]) => ({ gnss, value }));
	const entries = Utils.sortArrayByFieldAccordingToValuesOrder(rawEntries, 'gnss', Consts.GNSS_DATA_CODE_ORDER);
	return entries.map(stat => {
		const { gnss, value } = stat;
		const gnssData = getGnssDataDisplay(gnss);
		const heightBright = getRelativeColumnHeight(value.valueBright, value.minValue, value.maxValue);
		const valuePale = value.valuePale;
		const columnPaleVisible = valuePale != null && !allSubnetworksModeRef.value;
		const heightPale = columnPaleVisible ? getRelativeColumnHeight(valuePale, value.minValue, value.maxValue) : 0;
		let bottom: number;
		if (fullModeRef.value) {
			bottom = columnPaleVisible ? 15 : 0;
		} else {
			bottom = heightBright * 70;
		}
		const precision = (value.precision == null) ? 1 : value.precision;
		const precisionLess = Math.max(0, precision - 1);
		const formatter = Utils.formatFractions(precision);
		const formatterLess = Utils.formatFractions(precisionLess);
		return {
			gnss,
			gnssName: gnssData.name,
			valueBright: fullModeRef.value ? formatter(value.valueBright) : formatterLess(value.valueBright),
			valuePale: columnPaleVisible ? (fullModeRef.value ? formatter(valuePale) : formatterLess(valuePale)) : null,
			columnBrightStyle: {
				height: (heightBright * (fullModeRef.value ? 100 : 70)) + 'px',
				backgroundColor: '#' + gnssData.color,
			},
			columnPaleStyle: {
				height: (Math.min(heightBright, heightPale) * (fullModeRef.value ? 100 : 70)) + 'px',
				backgroundColor: '#' + gnssData.colorLight,
			},
			columnPaleVisible: columnPaleVisible,
			itemValueStyle: {
				left: fullModeRef.value ? '0' : '-2px',
				right: fullModeRef.value ? '0' : '-2px',
				bottom: bottom + 'px',
				fontSize: fullModeRef.value ? '11px' : '9px',
			},
			signals: value.signals,
			rtcmMessages: value.rtcmMessages,
			currentDatetimeBright: value.currentDatetimeBright,
			currentDatetimePale: value.currentDatetimePale,
			valueIsOld: value.valueIsOld,
		};
	});
};


export default {
	transformFromLonLat,
	transformFromLonLatArray,
	transformCoords,
	transformToLonLat,
	getPoint,
	getLine,
	getCircle,
	getRectangle,
	getServiceColors,
	getBaselineColor,
	getColor,
	getQualityColor,
	filterInsideViewport,
	filterVRSFeaturesRemoveDuplicateStations,
	checkIsFocusedMountpoint,
	getGnssDataDisplay,
	getGnssFixQualityDisplay,
	getRelativeColumnHeight,
	getGnssStatsDisplay,
};
